import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import React, { ReactElement, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import getFromDate from '~/ui/pages/MedicationLogistics/helpers/getFromDate';
import getToDate from '~/ui/pages/MedicationLogistics/helpers/getToDate';
import buildDate from '~/utils/date/buildDate';
import { extractErrorMessage } from '~/utils/error/error';
import sortByLabel from '~/utils/sorting/sortByLabel';
import useMedDeliveris from './hooks/useMedDeliveris';
import { medicationDeliveryValidationSchema } from './validates';

import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';
import DateRangePicker from '~/ui/components/inputs/DateRangePicker';
import Input from '~/ui/components/inputs/Input';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import MultiValueNoChipText from '~/ui/components/inputs/SelectWithoutAnimation/components/MultiValueNoChipText';
import TimePicker from '~/ui/components/inputs/TimePicker';
import MedicationChangeInfo from '~/ui/pages/MedicationLogistics/components/MedicationChangeInfo';
import MedicationDeliveryInfo from '~/ui/pages/MedicationLogistics/components/MedicationDeliveryInfo';

import { GroupTypeBase, Styles } from 'react-select';
import { IMedication } from '~/services/api/client/types';
import useQuery from '~/store/medicationLogistics/hooks/useQuery';
import useRole from '~/store/user/hooks/useRole';
import { IOption } from '~/types';
import { IOption as ISelectOption } from '~/ui/components/inputs/Select/types';
import { IDetails } from '../types';
import styles from './Styles.module.scss';
import { IMedicationDeliveryInitialValues, IRequestInfo } from './types';

interface IProps {
  defaultValues: IMedicationDeliveryInitialValues;
  requestInfo: IRequestInfo;
  details: IDetails | null;
  setModalTitle: (v: string | null) => void;
  setDetails: (v: IDetails | null) => void;
  showCurrentTeamVisits: boolean;
}

const AddMedicationDelivery = ({
  defaultValues,
  details,
  requestInfo,
  setDetails,
  setModalTitle,
  showCurrentTeamVisits,
}: IProps): ReactElement => {
  const { isGlobalUser, role } = useRole();
  const { current } = useStoreState(state => state.client);

  // GU should only see/select teams they're actually a member of, not every team the client is assigned to
  const availableActTeams = useMemo(
    () =>
      isGlobalUser
        ? current?.actTeams.filter(actTeam =>
            role?.teams?.some(team => team.id === actTeam.team.id),
          )
        : current?.actTeams,
    [current?.actTeams, isGlobalUser, role?.teams],
  );

  const isTeamRequired = isGlobalUser && availableActTeams?.length > 1;
  // for GU creating a new delivery the pre-filled teamId is a page-level guess, not a real
  // choice — clear it so the required validation actually forces an explicit team selection
  const initialValues =
    isTeamRequired && !defaultValues?.id ? { ...defaultValues, teamId: undefined } : defaultValues;

  const {
    control,
    formState: { errors },
    reset,
    register,
    handleSubmit,
    unregister,
    watch,
    setValue,
  } = useForm({
    defaultValues: initialValues,
    resolver: medicationDeliveryValidationSchema(isTeamRequired),
  });
  const teamId = watch('teamId');

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const {
    onGetMedicationDeliveries,
    onUpdateMedicationDelivery,
    onAddMedicationDelivery,
    setMedicationDelivery,
  } = useStoreActions(actions => actions.medicationLogistics);

  const currentMedicationDelivery = useStoreState(state => state.medicationLogistics.current);
  const query = useQuery();
  const selectedMonthQuery = query.get('selectedMonth');
  const refetchDate = selectedMonthQuery ? new Date(selectedMonthQuery) : null;

  useEffect(
    () => () => {
      setDetails(null);
      setModalTitle(null);
      setMedicationDelivery(null);
    },
    [setDetails, setMedicationDelivery, setModalTitle],
  );

  const medications = useMemo(
    () =>
      current.medications
        .reduce((acc: IOption[], obj: IMedication) => {
          if (!obj.isArchived && !obj.injectable) {
            let label = obj.medication.name;
            if (obj.dose) {
              label = `${label} (${obj.dose})`;
            }
            if (obj.outsideFacility) {
              label = `${label} <chip>O.F.</chip>`;
            }

            acc.push({
              label,
              value: obj.id,
            });
          }
          return acc;
        }, [])
        .sort(sortByLabel),
    [current.medications],
  );

  const { notDelivered, groupDelivery } = watch();

  // when a team must be chosen in-modal, team members must come from that selection,
  // not the page-level team the popup happened to open from
  const membersRequestInfo = isTeamRequired
    ? { ...requestInfo, teamId: teamId ? String(teamId) : '' }
    : requestInfo;
  const { members, reasons, loading } = useMedDeliveris(membersRequestInfo, !defaultValues?.id);

  const refetchDeliveries = (date: Date) => {
    onGetMedicationDeliveries({
      clinicId: String(requestInfo.clinicId),
      clientId: String(current.id),
      teamId: String(requestInfo.teamId),
      from: getFromDate(date) as string,
      to: getToDate(date) as string,
      currentTeamDeliveriesOnly: isGlobalUser ? false : showCurrentTeamVisits,
    });
  };

  const onSubmit = async (
    values: IMedicationDeliveryInitialValues,
    evt: React.BaseSyntheticEvent,
  ) => {
    const {
      time,
      date: medicationDeliveryDate,
      archiveMedication,
      hasAlerts,
      lastInjection,
      nextDueDate,
      notificationOn,
      notificationTime,
      teamMembersToNotify,
      statusChangeLog,
      dateRange,
      teamId: selectedTeamId,
      ...rest
    } = values;

    const isGroupDelivery = values.groupDelivery && !values.notDelivered;
    const groupStartDate = dateRange?.[0] ? new Date(dateRange[0]).toISOString() : undefined;

    const date = buildDate(
      time,
      isGroupDelivery && groupStartDate ? groupStartDate : medicationDeliveryDate,
    );

    const groupEndDate = dateRange?.[1]
      ? buildDate(time, new Date(dateRange[1]).toISOString())
      : undefined;

    const requestPayload = {
      ...rest,
      date,
      groupDelivery: isGroupDelivery,
      groupEndDate,
      clientId: current.id,
    };

    const { name } = (evt as React.BaseSyntheticEvent<HTMLFormElement>).nativeEvent.submitter;
    try {
      if (details && details.id) {
        const payload = {
          requestInfo: {
            ...requestInfo,
            teamId: selectedTeamId ? String(selectedTeamId) : requestInfo.teamId,
          },
          requestPayload: { ...requestPayload, medicationDeliveryId: String(details.id) },
        };
        await onUpdateMedicationDelivery(payload);
        setDetails(null);
      } else {
        const payload = {
          requestInfo: {
            ...requestInfo,
            teamId: selectedTeamId ? String(selectedTeamId) : requestInfo.teamId,
          },
          requestPayload,
        };
        await onAddMedicationDelivery(payload);
        reset({ notDelivered: false, id: null, note: '', medicationIds: [], teamMemberId: null });
      }

      refetchDeliveries(isGroupDelivery ? refetchDate : new Date(date));
      setModalTitle(name);
      setMedicationDelivery(null);

      const type = details?.id ? 'updated' : 'added';

      showNotify({ message: `Medication delivery successfully ${type}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    if (!notDelivered) {
      unregister(['nonDeliveryReasonId']);
    }
  }, [notDelivered, unregister]);

  const filteredMembers = useMemo(
    () =>
      members.filter(
        member => !member.isDisabled || defaultValues?.teamMemberId === (member.value as number),
      ),
    [members, defaultValues],
  );

  if (loading) return <Loader />;

  const fullName = `${current.firstName} ${current.lastName}`;

  const showSecondSubmit = !details?.id;

  const setAll = (all?: boolean) => {
    register('medicationIds');
    if (all)
      setValue(
        'medicationIds',
        medications.map(m => Number(m.value)),
      );
    else {
      setValue('medicationIds', []);
    }
  };

  const additionalStyleHandler = (): Partial<
    Styles<ISelectOption, boolean, GroupTypeBase<ISelectOption>>
  > => ({
    menuList: provided => ({ ...provided, height: '100%' }),
    menu: provided => ({ ...provided, minHeight: '50px', maxHeight: '200px' }),
  });

  const archivedTeams = availableActTeams?.filter(actTeam => actTeam.isArchived);
  const showArchivedTeamsOptions =
    teamId && archivedTeams.find(actTeam => actTeam.team.id === teamId);

  const getTeamOptions = () => {
    if (showArchivedTeamsOptions && !notDelivered) {
      return availableActTeams.map(actTeam => ({
        label: actTeam.team.name,
        value: actTeam.team.id,
      }));
    }

    return availableActTeams
      ?.filter(actTeam => !actTeam.isArchived)
      .map(actTeam => ({
        label: actTeam.team.name,
        value: actTeam.team.id,
      }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <MedicationDeliveryInfo
            name={fullName}
            medication={currentMedicationDelivery}
            fullInfo={!!details?.id}
          />
        </Grid>
        {isTeamRequired && (
          <Grid item sm={12}>
            <SelectComponent
              options={getTeamOptions()}
              name="teamId"
              control={control}
              errors={errors}
              label="Select Team"
              hideSelectedOptions={false}
              isDisabled={
                (showArchivedTeamsOptions && !notDelivered) ||
                (!!defaultValues.id && !!defaultValues.teamId)
              }
            />
          </Grid>
        )}
        {!currentMedicationDelivery && (
          <Grid item sm={12}>
            <SelectComponent
              isMulti
              closeMenuOnSelect={false}
              showCustomComponents
              showSelectAll
              hideSelectedOptions={false}
              name="medicationIds"
              options={medications}
              additionalStyleHandler={() => additionalStyleHandler()}
              control={control}
              errors={errors}
              label="Select Medication"
              isDisabled={!!defaultValues?.id}
              setAll={setAll}
              customComponents={{
                MultiValue: MultiValueNoChipText,
              }}
            />
          </Grid>
        )}

        {!defaultValues?.groupDelivery && (
          <Grid item sm={6}>
            <Checkbox
              size="small"
              name="notDelivered"
              control={control}
              errors={errors}
              label="Medication not delivered"
            />
          </Grid>
        )}
        <Grid item sm={6}>
          <Checkbox
            size="small"
            name="outsideFacility"
            control={control}
            errors={errors}
            label="Outside Facility"
          />
        </Grid>
        {defaultValues.statusChangeLog && (
          <Grid item sm={12}>
            <MedicationChangeInfo
              delivered={defaultValues.statusChangeLog.delivered}
              when={defaultValues.statusChangeLog.date}
              changedBy={defaultValues.statusChangeLog.user.name}
            />
          </Grid>
        )}
        {notDelivered && (
          <Grid item sm={12}>
            <SelectComponent
              options={reasons}
              name="nonDeliveryReasonId"
              control={control}
              errors={errors}
              label="Select Reason"
              hideSelectedOptions={false}
            />
          </Grid>
        )}
        <Grid item sm={12}>
          <SelectComponent
            options={filteredMembers}
            name="teamMemberId"
            control={control}
            errors={errors}
            label="Select Responsible Team Member"
            hideSelectedOptions={false}
            isDisabled={isTeamRequired && !teamId}
          />
        </Grid>
        <Grid item sm={12}>
          <Input name="note" register={register} label="Note" multiline errors={errors} />
        </Grid>
        <Grid item sm={6}>
          {groupDelivery && !notDelivered ? (
            <DateRangePicker
              label="Select Date Range"
              control={control}
              name="dateRange"
              errors={errors}
            />
          ) : (
            <DatePicker
              name="date"
              openTo="date"
              control={control}
              errors={errors}
              label="Delivery Date"
              maxDate={new Date().toString()}
            />
          )}
        </Grid>

        <Grid item sm={6}>
          <TimePicker name="time" control={control} label="Delivery Time" errors={errors} />
        </Grid>
        {!notDelivered && (defaultValues?.groupDelivery || !defaultValues?.id) && (
          <Grid item sm={12}>
            <Checkbox
              size="small"
              name="groupDelivery"
              control={control}
              errors={errors}
              label="Deliver for a date range"
              disabled={!!defaultValues?.id}
            />
          </Grid>
        )}
      </Grid>
      <div className={classNames(styles.buttonsWrapper, styles.marginVertical)}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setMedicationDelivery(null);
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          className={styles.margin}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddMedicationDelivery;
